/**
 * Password validation utilities
 */

// Password requirements configuration (must match backend auth.config.js)
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  maxLength: 100,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
};

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates password strength
 * Requirements are based on PASSWORD_REQUIREMENTS configuration
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  const errors: string[] = [];

  if (!password || password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`);
  }

  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (PASSWORD_REQUIREMENTS.requireNumbers && !/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (PASSWORD_REQUIREMENTS.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * React Admin validator for password field
 */
export const passwordValidator = (value: string) => {
  if (!value) {
    return 'Password is required';
  }

  const result = validatePassword(value);
  if (!result.isValid) {
    return result.errors.join('. ');
  }

  return undefined;
};

/**
 * React Admin validator for password field (optional - for edit forms)
 */
export const optionalPasswordValidator = (value: string) => {
  // Allow empty password (don't change it)
  if (!value || value.trim() === '') {
    return undefined;
  }

  const result = validatePassword(value);
  if (!result.isValid) {
    return result.errors.join('. ');
  }

  return undefined;
};
